import React, { useEffect, useState, createRef } from 'react';
import { ListGroup, Dropdown, Form } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { capitalize, convertdatenotime } from './utils';
import { postData, deleteData, putData } from './Api';
import { BsPlus } from 'react-icons/bs';
import * as Papa from 'papaparse';
import ModalItem from './Modal';
import ModalFile from './ModalFile';
import AlertCustom from '../common/AlertCustom';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';


const ItemList = ({ data, cols, baseUrl, setData, fields, disableClick, testDataFname }) => {

    const [selectedItem, setSelectedItem] = useState({});
    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [showUpdateItemModal, setShowUpdateItemModal] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [showFileUploadModal, setShowFileUploadModal] = useState(false);
    const [items, setItems] = useState(data); // Local state for reordered items
    const windowUrl = baseUrl.replace('/api/v1','');
    const inputRef = createRef();
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);

    useEffect(() => {
        setItems(data);
    }, [data]);

    useEffect(() => {
        if (Object.keys(selectedItem).length > 0) {
            setShowUpdateItemModal(true);
        }
    }, [selectedItem]);

    useEffect(() => {
        if (!showUpdateItemModal) {
            setSelectedItem({});
        }
    }, [showUpdateItemModal]);

    const addItem = async (item) => { 
        try {
            const response = await postData(baseUrl, item);
            if (response.status === 201 || response.status === 200) {
                setData();
            } 
        } catch (error) {
            if (error.response.status === 400){
                setError('Wrong format for a field, check your data.' + error.response?.data?.message);
                setShowAlert(true);
            } else {
                setError('Failed to add item. ' + error.response?.data?.message);
                setShowAlert(true);
            }
        }
    };
    
    const deleteItem = async (item) => { 
        try {
            const response = await deleteData(`${baseUrl}/${item.id}`);
            if (response.status === 201 || response.status === 200) {
                setData();
            }  
        } catch (error) {
            setError('Failed to delete item ' + error.response?.data?.message);
            setShowAlert(true);
        }
    };

    const updateItem = async (item) => { 
        try {
            const response = await putData(`${baseUrl}/${item.id}`, item);
            if (response.status === 201 || response.status === 200) {
                setData();
            }
        } catch (error) {
            setError('Failed to update item ' + error.response?.data?.message);
            setShowAlert(true);
        }
    };
    
    const handleUpload = () => {
        setSelectMode(false);
        setCheckedAll(false);
        setSelectedItems([]);
        inputRef.current?.click();
    };

    const updateData = async (result) => {
        var data = result.data;
        var lastElement = Object.keys(data[data.length-1])[0];
        if (data[data.length-1][lastElement] === '') {
            data.pop();
        }
        setFileData(data);
        setShowFileUploadModal(true);
    };

    const handleInputFile = (e) => {
        if (e.target.files.length > 0){
            Papa.parse(inputRef.current.files[0], { 
                complete: updateData, 
                header: true, 
            });
        }

        // Reset the input field so selecting the same file again triggers the event
        e.target.value = "";
    };

    const handleLoadTestData = async () => {
        try {
            const response = await fetch(testDataFname);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const jsonData = await response.json();
            updateData({data: jsonData});
        } catch (error) {
            console.error(error)
        }
    };

    const addDataFromFile = (mappedColumns) => {
        fileData.forEach(row => {
            var newObj = {};
            mappedColumns.forEach(element => {
                const result = cols.filter(col => col.accessor === element.attribute);
                const isDateType = result.length === 1 ? result[0].dateType : false;
                if (isDateType){
                    // validate formatting of the date
                    const date = new Date(row[element.mapped]);
                    if (date.toString() === 'Invalid Date') {
                        setError(`Invalid date format for ${element.attribute}, must be YYYY-MM-DD. Using null instead.`);
                        setShowAlert(true);
                        newObj[element.attribute] = null;
                    } else {
                        newObj[element.attribute] = date.toISOString().slice(0, 10);
                    }
                } else {
                    newObj[element.attribute] = element.mapped === '' ? null : row[element.mapped];
                }
            });
            addItem(newObj);
        });
    };

    const bulkUpdate = async (reorderedItems) => {
        try {
            const updatedData = reorderedItems.map((item, index) => ({
                id: item.id,
                order_index: index
            }));
    
            // Send a single bulk update request
            await putData(`${baseUrl}/update_order`, updatedData);
            setData();
        } catch (error) {
            console.error("Bulk update failed", error);
            setError(error);
            setShowAlert(true);
        }
    };

    // Function to handle drag-and-drop
    const onDragEnd = (result) => {
        if (!result.destination) return;
        const reorderedItems = Array.from(items);
        const [movedItem] = reorderedItems.splice(result.source.index, 1);
        reorderedItems.splice(result.destination.index, 0, movedItem);
        setItems(reorderedItems);
        bulkUpdate(reorderedItems);
    };

    const deleteSelectedItems = async () => {
        try {
            await Promise.all(selectedItems.map(id => deleteData(`${baseUrl}/${id}`)));
            setData();
            setSelectedItems([]);
            setSelectMode(false);
        } catch (error) {
            setError('Failed to delete selected items');
            setShowAlert(true);
        }
    };

    const handleSelection = (id) => {
        setSelectedItems(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
        setCheckedAll(false);
    };

    return (
        <div>
            {selectMode && (
                <div className='d-flex m-2'>
                <Form.Check style={{marginRight: '0.5rem'}}
                    type="checkbox" 
                    checked={checkedAll}
                    onChange={() => {
                        setCheckedAll(!checkedAll);
                        setSelectedItems(checkedAll ? [] : items.map(item => item.id));
                    }}
                /> <p className='mb-0'>Select all</p> </div>
                )} 
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="items">
                    {(provided) => (
                        <ListGroup {...provided.droppableProps} ref={provided.innerRef}>
                            {items.map((item, index) => (
                                <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                    {(provided) => (
                                        <ListGroup.Item 
                                            ref={provided.innerRef} 
                                            {...provided.draggableProps} 
                                            {...provided.dragHandleProps}
                                            className="d-flex align-items-center">
                                            {selectMode && (
                                                <Form.Check style={{marginRight: '0.5rem'}}
                                                    type="checkbox" 
                                                    checked={selectedItems.includes(item.id) || checkedAll} 
                                                    onChange={() => handleSelection(item.id)}
                                                />
                                            )}
                                            <div onClick={() => !disableClick && !selectMode && window.open(`${windowUrl}/${item.id}`,"_self")}>
                                                <h5>{item.name}</h5>
                                                {Object.keys(item).filter(field =>
                                                    field !== 'created_at' 
                                                    && field !== 'id' 
                                                    && !field.includes('_id') 
                                                    && field !== 'name' 
                                                    && field !== 'order_index'
                                                ).map(field => (
                                                    <p key={field} className="mb-1">{capitalize(field)}: {item[field] === null ? 'none' : item[field]}</p>
                                                ))}
                                                <small>Created at: {convertdatenotime(item.created_at)}</small>
                                            </div>
                                            {!selectMode && (
                                            <Dropdown style={{marginLeft: 'auto'}}>
                                                <Dropdown.Toggle as="button" className="btn p-0"></Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => setSelectedItem(item)}>Edit</Dropdown.Item>
                                                    <Dropdown.Item onClick={() => deleteItem(item)}>Delete</Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown> )}
                                        </ListGroup.Item>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </ListGroup>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Modals */}
            <ModalItem 
                item={selectedItem} 
                itemFields={fields}
                baseUrl={baseUrl}
                onClickAction={updateItem}
                showModal={showUpdateItemModal}
                setShowModal={setShowUpdateItemModal}
            />

            <ModalItem 
                item={{}}
                itemFields={fields}
                baseUrl={baseUrl}
                onClickAction={addItem}
                showModal={showAddItemModal}
                setShowModal={setShowAddItemModal}
            />

            {/* Add new item or upload from file */}
            <div className='justify-content-between'>
            <div className='btn-group-custom btn-container'>
                <button className='btn-custom green' onClick={() => setShowAddItemModal(true)}> 
                    <BsPlus size={20} className="me-2" /> Add New
                </button>
                <input 
                    ref={inputRef} 
                    onChange={handleInputFile} 
                    type="file" 
                    id="input-file" 
                    style={{ display: "none" }} 
                    accept="csv"
                />
                <button className='btn-custom blue' onClick={handleUpload}>Upload File</button>                
            </div>
            {data.length > 0 ? <div>
                <div className='btn-group-custom btn-container'>
                {selectMode && (
                    <IconButton style={{backgroundColor: 'transparent'}} 
                        onClick={deleteSelectedItems} 
                        disabled={selectedItems.length === 0}>
                        <DeleteIcon color={selectedItems.length === 0 ? "disabled" : "error"} 
                        />
                    </IconButton>
                )}
                <button className='btn-custom gray' onClick={() => setSelectMode(!selectMode)}> 
                        {selectMode ? 'Cancel' : 'Select'}
                </button>
                
                </div>
                </div> : null }
            </div>

            <ModalFile 
                cols={cols}
                data={fileData}
                onSaveClickAction={addDataFromFile}
                showModal={showFileUploadModal}
                setShowModal={setShowFileUploadModal}
            />

            <AlertCustom 
                text={error}
                variant="danger"
                showAlert={showAlert}
                setShowAlert={setShowAlert}
            />

            <div className='m-2'>
            <Form.Text className="text-muted">
                Upload example data <span onClick={() => 
                handleLoadTestData()} className="underlined-text">here</span>. 
            </Form.Text> 
            </div>

        </div>
    );
};

export default ItemList;
